import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface Pack {
  id: string;
  name: string;
  price: number;
  words: number;
}

const Credits: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [balance, setBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tiers, bal] = await Promise.all([
          axios.get('/subscriptions/tiers'),
          axios.get('/tokens/balance'),
        ]);
        const list: Pack[] = tiers.data.topUpPacks || [];
        setPacks(list);
        setSelected((s) => s || list[1]?.id || list[0]?.id || '');
        setBalance(Number(bal.data.currentBalance) || 0);
      } catch {
        toast.error(t('creditsTopUp.loadError', 'Could not load top-up packs'));
      }
    })();
  }, [t]);

  useEffect(() => {
    if (params.get('checkout') === 'success') {
      toast.success(t('creditsTopUp.success', 'Words added — happy writing.'));
    }
  }, [params, t]);

  const perThousand = (p: Pack) => ((p.price / p.words) * 1000).toFixed(2);
  const best = packs.reduce<Pack | null>(
    (b, p) => (!b || p.price / p.words < b.price / b.words ? p : b),
    null,
  );

  const purchase = async () => {
    const pack = packs.find((p) => p.id === selected);
    if (!pack) return;
    setIsLoading(true);
    try {
      const res = await axios.post('/subscriptions/top-up', {
        packId: pack.id,
        successUrl: `${window.location.origin}/credits?checkout=success`,
        cancelUrl: `${window.location.origin}/credits`,
      });
      if (res.data.sessionUrl) window.location.href = res.data.sessionUrl;
      else
        toast.error(t('creditsTopUp.sessionError', 'Could not start checkout. Please try again.'));
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          t('creditsTopUp.sessionError', 'Could not start checkout. Please try again.'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-light-border dark:border-dark-border">
          <button
            onClick={() => navigate(-1)}
            aria-label={t('common.back', 'Back')}
            className="p-2 hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
          </button>
          <h1 className="text-lg font-semibold text-light-text dark:text-dark-text">
            {t('credits.title', 'Top up AI words')}
          </h1>
          <div className="w-9" />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-light-surface dark:bg-dark-surface px-4 py-2 rounded-full">
              <StarIcon className="w-5 h-5 text-yellow-500" />
              <span className="text-light-text-secondary dark:text-dark-text-secondary text-sm">
                {t('creditsTopUp.currentBalance', 'AI words left:')}
              </span>
              <span className="text-light-text dark:text-dark-text font-semibold">
                {balance.toLocaleString()}
              </span>
            </div>
          </div>

          <p className="text-center text-light-text-secondary dark:text-dark-text-secondary text-sm mb-6">
            {t('credits.description', 'Top-up words never expire and stack on top of your plan.')}
          </p>

          <div className="space-y-3 mb-8">
            {packs.map((pack) => (
              <button
                key={pack.id}
                type="button"
                onClick={() => setSelected(pack.id)}
                aria-pressed={selected === pack.id}
                className={`w-full p-4 rounded-2xl border-2 transition-all ${
                  selected === pack.id
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-light-border dark:border-dark-border bg-white dark:bg-dark-surface hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <StarIcon className="w-4 h-4 text-yellow-500" />
                      <span className="text-light-text dark:text-dark-text font-semibold">
                        {t('credits.wordsCount', '{{words}} AI words', {
                          words: pack.words.toLocaleString(),
                        })}
                      </span>
                      {best?.id === pack.id && (
                        <span className="bg-green-500/20 text-green-800 dark:text-green-300 text-xs px-2 py-0.5 rounded-full">
                          {t('creditsTopUp.bestValue', 'Best value')}
                        </span>
                      )}
                    </div>
                    <p className="text-light-text-secondary dark:text-dark-text-secondary text-xs mt-1">
                      {pack.name} · ${perThousand(pack)} / {t('credits.perThousand', '1,000 words')}
                    </p>
                  </div>
                  <p className="text-light-text dark:text-dark-text font-bold text-lg">
                    ${pack.price.toFixed(2)}
                  </p>
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={purchase}
            disabled={isLoading || !selected}
            className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-2xl transition-colors flex items-center justify-center gap-2"
          >
            <StarIcon className="w-5 h-5" />
            {isLoading
              ? t('creditsTopUp.processing', 'Processing...')
              : t('creditsTopUp.addCredits', 'Add words')}
          </button>

          <button
            type="button"
            onClick={() => navigate('/subscription')}
            className="block mx-auto mt-4 text-sm text-primary-500 underline"
          >
            {t('creditsTopUp.planInstead', 'Writing a lot? A plan refills every month')}
          </button>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Credits;
